<template>
  <div class="container flex c-flex-wrap">
    <!-- Reviews list -->
    <div class="reviews row c-justify-center c-lg:c-justify-between order-0 c-md:order-0 c-flex-grow-1">
      <!-- Top comment -->
      <ProductTopReviewItem
        v-for="topComment in topComments"
        :key="topComment.authorable_id"
        :comment="topComment"
      />
      <!-- Comments -->
      <div
        id="reviews"
        class="col-12 c-sm:col-10 c-md:col-9 c-lg:col-6 c-lg:block c-lg:c-pl-5"
      >
        <ul class="list-unstyled c-mb-0">
          <ReviewsItem
            v-for="(computedComment) in structuredComments.slice(0, commentsLimit)"
            :key="computedComment.authorable_id"
            :comment="computedComment"
          />
        </ul>
      </div>
      <div class="col-12 flex c-justify-center c-mt-3 text-xs-c">
        <button
          v-if="showMoreButton && comments.length > 3"
          type="button"
          class="hidden c-lg:flex btn  btn-primary c-py-2 c-px-5 font-medium c-rounded-pill"
          @click="showMore"
        >
          {{ t('load_more_comments') }} ({{ comments.length > 99 ? '+99' : comments.length - commentsLimit }})
        </button>
      </div>

      <!-- All comments and write comment -->
      <div class="col-12 c-sm:col-10 c-md:col-9 c-lg:hidden c-mt-3">
        <ul class="form-row c-mb-0 list-unstyled">
          <li class="col-auto c-flex-grow-1">
            <button
              v-if="showMoreButton"
              type="button"
              class="btn  btn-primary c-px-4 c-w-100 c-rounded-pill text-xs-c font-medium"
              data-toggle="collapse"
              data-target="#reviews:not(.show)"
              aria-expanded="false"
              aria-controls="reviews"
              @click="showMore"
            >
              {{ t('show_more_comments') }}
            </button>
          </li>
        </ul>
      </div>
    </div>
    <ReviewForm :product="product" />
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ReviewForm from '@/components/general/BaseReviewForm.vue'
import ReviewsItem from '@/components/general/BaseReviewsItem.vue'
import ProductTopReviewItem from '@/components/products/ProductTopReviewItem.vue'

const { t } = useI18n()

const props = defineProps({
  product: {
    type: Object,
    required: true,
  },

  topComments: {
    type: Array,
    required: true,
  },

  comments: {
    type: Array,
    required: true,
  },
})

const commentsLimit = ref(3)
const showMoreButton = ref(true)

const groupedCommentsByParent = computed(() => {
  const groups = {}
  for (const comment of props.comments) {
    const parentId = comment.parent_id || 0
    if (!groups[parentId]) groups[parentId] = []
    groups[parentId].push(comment)
  }
  return groups
})

const structuredComments = computed(() => {
  const result = []

  const walk = (parentId = 0, depth = 0) => {
    const children = groupedCommentsByParent.value[parentId] || []
    for (const comment of children) {
      result.push({ ...comment, _depth: depth })
      walk(comment.id, depth + 1)
    }
  }

  walk()
  return result
})

const showMore = () => {
  commentsLimit.value = commentsLimit.value + 10
  if (commentsLimit.value >= props.comments.length) showMoreButton.value = false
}
</script>

<i18n>
  {
    "cs": {
    "load_more_comments": "Načíst další komentáře",
    "show_more_comments": "Zobrazit více"
},
    "sk": {
    "load_more_comments": "Načítať ďalšie komentáre",
    "show_more_comments": "Zobraziť viac"
},
    "pl": {
    "load_more_comments": "Zobacz więcej komentarzy",
    "show_more_comments": "Zobacz więcej"
},
    "hu": {
    "load_more_comments": "Még több hozzászólás",
    "show_more_comments": "Mutass többet"
},
    "de": {
    "load_more_comments": "Weitere Kommentare laden",
    "show_more_comments": "Mehr anzeigen"
},
    "at": {
    "load_more_comments": "Weitere Kommentare laden",
    "show_more_comments": "Mehr anzeigen"
},
    "ro": {
    "load_more_comments": "Mai multe opinii",
    "show_more_comments": "Arată mai mult"
},
    "fr": {
    "load_more_comments": "Afficher plus de commentaires",
    "show_more_comments": "Afficher plus"
},
    "it": {
    "load_more_comments": "Carica altri commenti",
    "show_more_comments": "Mostra altro"
}
  }
</i18n>
